import { ILegalEntityRequestSchema } from '@monetize/types/app';
import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormWatch,
} from 'react-hook-form';
import { useId } from 'react';
import { BsTrash } from 'react-icons/bs';
import {
  MBadge,
  MBox,
  MButton,
  MCustomIconButton,
  MFlex,
  MFormField,
  MGridItem,
  MSwitch,
  TemplateExpressionHelp,
} from '../../../../components/Monetize';
import { TemplateExpressionWarning } from '../../../../components/Monetize/TemplateExpressionWarning';
import { MCustomMultiSelect } from '../../../../components/Monetize/MCustomSelect/MCustomMultiSelect';
import MEditor from '../../../../components/Monetize/MEditor';
import { useCurrencies } from '../../../../hooks/useCurrencies';
import { useConfirmModal } from '../../../../services/confirmModal';

interface InvoiceTermsEditorProps {
  control: Control<ILegalEntityRequestSchema>;
  errors: FieldErrors<ILegalEntityRequestSchema>;
  isReadOnly?: boolean;
  watch: UseFormWatch<ILegalEntityRequestSchema>;
}

export const LegalEntityInvoiceTerms = ({
  control,
  errors,
  isReadOnly = false,
  watch,
}: InvoiceTermsEditorProps) => {
  const FIELD_ARRAY_NAME = 'options.invoiceTerms.currencyTerms';
  // Each term's "active" switch sits in its "Select Currencies" field, before
  // the currency picker. Without its own id it takes the field's control id,
  // so the field label toggled the switch instead of naming the picker.
  const activeSwitchIdPrefix = useId();
  const { showConfirmModal, closeModal: closeConfirmationModal } =
    useConfirmModal();

  const { fields, append, remove } = useFieldArray({
    control,
    name: FIELD_ARRAY_NAME,
  });

  const handleAddCurrencyTerm = () => {
    append({ active: true, currencies: [], terms: '' }, { shouldFocus: false });
  };

  const { currencies } = useCurrencies();
  const currencyOptions = currencies.map((currency) => ({
    value: currency.code,
    title: `${currency.code} - ${currency.description}`,
  }));

  const handleRemoveCurrencyTerm = (index: number) => {
    showConfirmModal({
      title: 'Delete Invoice Terms',
      description: 'Are you sure you want to delete this invoice term?',

      onYes: () => {
        remove(index);
        closeConfirmationModal();
      },
      yesBtnProps: {
        variant: 'primary',
      },
      noBtnProps: {
        variant: 'cancel',
      },
    });
  };

  return (
    <>
      {fields.map((field, index) => (
        <MGridItem colSpan={12} key={field.id}>
          <MBox
            border="1px solid"
            borderColor="tGray.support"
            borderRadius="md"
            p={4}
            mb={4}
          >
            <MFlex justify="space-between">
              <MBox flex={2}>
                <MFormField
                  label="Select Currencies"
                  mb={4}
                  error={
                    errors?.options?.invoiceTerms?.currencyTerms?.[index]
                      ?.currencies
                  }
                  labelRightComponent={
                    <Controller
                      name={`${FIELD_ARRAY_NAME}.${index}.active`}
                      control={control}
                      defaultValue={false}
                      render={({ field: { value, onChange, ...rest } }) => (
                        <MSwitch
                          id={`${activeSwitchIdPrefix}-active-${index}`}
                          size="sm"
                          isChecked={value}
                          isDisabled={isReadOnly}
                          onChange={(e) => {
                            const { checked } = e.target;
                            onChange(checked);
                          }}
                          {...rest}
                        />
                      )}
                    />
                  }
                  isRequired
                >
                  <Controller
                    name={`${FIELD_ARRAY_NAME}.${index}.currencies`}
                    control={control}
                    render={({ field: { value, onChange, ...rest } }) => {
                      return (
                        <>
                          <MCustomMultiSelect
                            multiple
                            closeButtonText="Apply"
                            placeholder="Select currencies"
                            items={currencyOptions}
                            itemTitle="title"
                            itemValue="value"
                            value={value || []}
                            onChange={onChange}
                            isDisabled={
                              isReadOnly ||
                              !watch(FIELD_ARRAY_NAME)?.[index]?.active
                            }
                            {...rest}
                          />
                          <MFlex mt={1}>
                            {value.length > 1 &&
                              value.map((currency) => (
                                <MBadge
                                  key={currency}
                                  mr={1}
                                  px={2}
                                  py={1}
                                  color="tBlue.light"
                                >
                                  {currency}
                                </MBadge>
                              ))}
                          </MFlex>
                        </>
                      );
                    }}
                  />
                </MFormField>

                <MFormField
                  label="Terms for selected currencies"
                  error={
                    errors?.options?.invoiceTerms?.currencyTerms?.[index]?.terms
                  }
                  isRequired
                >
                  <Controller
                    name={`${FIELD_ARRAY_NAME}.${index}.terms`}
                    control={control}
                    render={({ field: { value, onChange, ...rest } }) => (
                      <>
                        <MEditor
                          autoScroll
                          handleEditorChange={onChange}
                          boxProps={{ pb: 0, minH: 260 }}
                          init={{ height: 260, width: '100%' }}
                          disabled={
                            isReadOnly ||
                            !watch(FIELD_ARRAY_NAME)?.[index]?.active
                          }
                          value={value || ''}
                          {...rest}
                        />
                        <TemplateExpressionWarning value={value} />
                        <TemplateExpressionHelp
                          documentType="invoice"
                          testId={`legal-entity-invoice-terms-help-btn-${index}`}
                        />
                      </>
                    )}
                  />
                </MFormField>
              </MBox>
              <MCustomIconButton
                variant="icon"
                ml={4}
                icon={BsTrash}
                aria-label="Delete"
                onClick={() => handleRemoveCurrencyTerm(index)}
                iconColor="tRed.base"
                isDisabled={isReadOnly}
              />
            </MFlex>
          </MBox>
        </MGridItem>
      ))}

      <MGridItem colSpan={12}>
        <MButton
          size="sm"
          variant="tertiary"
          isDisabled={isReadOnly}
          onClick={handleAddCurrencyTerm}
        >
          + Add Invoice Terms
        </MButton>
      </MGridItem>
    </>
  );
};
